import type { ButtonProps } from "@mui/material";
import { ClickAwayListener, MenuList, Paper, Popper } from "@mui/material";
import React, { type FC } from "react";
import type { NavLinkProps } from "react-router";

import { useHoverDropdown } from "~/hooks/useHoverDropdown";
import { useScreenResolution } from "~/hooks/useScreenResolution";

import * as Styled from "./MoreButton.styles";

export interface MoreButtonProps extends Omit<
  ButtonProps,
  "children" | "endIcon"
> {
  text: string;
  open?: boolean;
  isActive?: boolean;
  children?: React.ReactNode;
  arrowIcon?: boolean;
  to?: NavLinkProps["to"];
  prefetch?: NavLinkProps["prefetch"];
}

export const MoreButton: FC<MoreButtonProps> = ({
  text,
  open: externalOpen = false,
  isActive = false,
  children,
  arrowIcon = true,
  ...props
}) => {
  const hasDropdown = Boolean(children);
  const { isMobile, isDesktop } = useScreenResolution();

  const {
    anchorEl,
    open: internalOpen,
    popperContentRef,
    handleClose,
    handleTriggerClick,
    handleTriggerMouseEnter,
    handleTriggerMouseLeave,
    handlePopperMouseLeave,
  } = useHoverDropdown({ isMobile, isDesktop });

  const open = hasDropdown ? internalOpen : externalOpen;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasDropdown && isMobile) {
      handleTriggerClick(event);
    }
  };

  const button = (
    <Styled.Root
      {...props}
      $isActive={isActive}
      {...(hasDropdown && {
        ...(isMobile && { onClick: handleClick }),
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleTriggerMouseLeave,
      })}
      endIcon={arrowIcon && <Styled.ArrowIcon $open={open} />}
    >
      <Styled.Text variant="caption" $isActive={isActive}>
        {text}
      </Styled.Text>
    </Styled.Root>
  );

  if (!hasDropdown) {
    return button;
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Styled.DropdownRoot>
        {button}
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 1,
            pointerEvents: open ? "auto" : "none",
          }}
        >
          <Paper ref={popperContentRef} onMouseLeave={handlePopperMouseLeave}>
            <MenuList autoFocusItem={isMobile && open} onClick={handleClose}>
              {children}
            </MenuList>
          </Paper>
        </Popper>
      </Styled.DropdownRoot>
    </ClickAwayListener>
  );
};
